/**
 * networkMonitor — lightweight external store for network status.
 * Tracks in-flight slow requests and offline state.
 * Lives outside React so axios interceptors can call it directly.
 */

type Listener = () => void;

let slowRequestCount = 0;
let isOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false;

const listeners = new Set<Listener>();

const notify = () => listeners.forEach(l => l());

export const networkMonitor = {
  getSlowRequestCount: () => slowRequestCount,
  getIsOffline: () => isOffline,

  incrementSlow: () => {
    slowRequestCount++;
    notify();
  },

  decrementSlow: () => {
    slowRequestCount = Math.max(0, slowRequestCount - 1);
    notify();
  },

  setOffline: (offline: boolean) => {
    if (isOffline !== offline) {
      isOffline = offline;
      notify();
    }
  },

  subscribe: (listener: Listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

// Wire up browser online/offline events once on module load
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => networkMonitor.setOffline(false));
  window.addEventListener('offline', () => networkMonitor.setOffline(true));
}

/** How long (ms) before a pending request is considered "slow" */
export const SLOW_REQUEST_THRESHOLD_MS = 2000;
